import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  GitFork,
  LayoutGrid,
  ListTree,
  Milestone,
  Network,
  Plus,
  Share2,
  Trash2,
  Pencil,
  Upload,
  Workflow,
} from "lucide-react";
import { MAP_KIND_LABELS, type MapKind, type MindmapSummary } from "@etus-mindmap/shared/api";
import { useCreateMap, useDeleteMap, useMaps, useSpaces, useUpdateMap } from "../api/hooks";
import { useActiveSpace } from "../api/space-context";
import { Drawer } from "../components/Drawer";
import { NewMapModal } from "../components/NewMapModal";
import { parseMapExport } from "../lib/export";

const KIND_ICON: Record<MapKind, typeof Network> = {
  mindmap: Network,
  "mindmap-bi": GitFork,
  org: ListTree,
  timeline: Milestone,
  matrix: LayoutGrid,
  concept: Share2,
  flow: Workflow,
};

export function KindIcon({ kind, className }: { kind: MapKind; className?: string }) {
  const Icon = KIND_ICON[kind];
  return <Icon className={className ?? "h-4 w-4"} aria-label={MAP_KIND_LABELS[kind]} />;
}

export function MapsRoute() {
  const maps = useMaps();
  const deleteMap = useDeleteMap();
  const spaces = useSpaces();
  const { activeSpace } = useActiveSpace();
  const spaceName =
    activeSpace.type === "personal"
      ? "Pessoal"
      : activeSpace.type === "shared"
      ? "Compartilhados comigo"
      : activeSpace.type === "org"
      ? spaces.data?.orgs.find((o) => o.id === activeSpace.id)?.name ?? "Organização"
      : spaces.data?.teams.find((t) => t.id === activeSpace.id)?.name ?? "Time";
  // No espaço "compartilhados comigo" não se cria mapa nem se gerencia (só abre).
  const shared = activeSpace.type === "shared";
  const [editing, setEditing] = useState<MindmapSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const createMap = useCreateMap();
  const fileInput = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Importa um .etusmind.json: valida o envelope, cria um novo mapa (no espaço
  // ativo) já com o conteúdo e abre o editor. Round-trip do downloadMapJson.
  const onImportFile = async (file: File | null) => {
    if (fileInput.current) fileInput.current.value = ""; // permite reimportar o mesmo arquivo
    if (!file) return;
    setImportError(null);
    try {
      const parsed = parseMapExport(await file.text());
      const created = await createMap.mutateAsync({ title: parsed.title, kind: parsed.kind, doc: parsed.doc });
      navigate({ to: "/maps/$uuid", params: { uuid: created.uuid } });
    } catch (e) {
      setImportError((e as Error).message);
    }
  };

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Mapas</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{spaceName}</p>
        </div>
        {!shared && (
          <div className="flex items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => void onImportFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              disabled={createMap.isPending}
              onClick={() => fileInput.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
            >
              <Upload className="h-4 w-4" aria-hidden />
              Importar
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Novo mapa
            </button>
          </div>
        )}
      </div>
      {importError && (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          Falha ao importar: {importError}
        </p>
      )}

      {maps.isLoading && <p className="text-sm text-slate-500 dark:text-slate-400">Carregando…</p>}
      {maps.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Erro ao carregar mapas: {(maps.error as Error).message}
        </p>
      )}
      {maps.data && maps.data.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {shared ? "Nenhum mapa compartilhado com você ainda." : "Nenhum mapa ainda — clique em “Novo mapa”."}
        </p>
      )}

      {maps.data && maps.data.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">Título</th>
                <th className="px-4 py-2.5 font-medium">Atualizado</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {maps.data.map((m) => (
                <tr key={m.uuid} className="bg-white hover:bg-primary/5 dark:bg-slate-950 dark:hover:bg-primary/10">
                  <td className="px-4 py-2.5">
                    <Link
                      to="/maps/$uuid"
                      params={{ uuid: m.uuid }}
                      className="inline-flex items-center gap-2 font-medium text-slate-900 hover:text-primary dark:text-slate-100 dark:hover:text-primary-light"
                    >
                      <KindIcon
                        kind={m.kind}
                        className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500"
                      />
                      {m.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">
                    {new Date(m.updatedAt * 1000).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5">
                    {!shared && (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(m)}
                          title="Editar"
                          aria-label={`Editar ${m.title}`}
                          className="flex h-7 w-7 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteMap.mutate(m.uuid)}
                          title="Excluir"
                          aria-label={`Excluir ${m.title}`}
                          className="flex h-7 w-7 items-center justify-center rounded text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-950 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditDrawer map={editing} onClose={() => setEditing(null)} />
      {creating && <NewMapModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function EditDrawer({ map, onClose }: { map: MindmapSummary | null; onClose: () => void }) {
  if (!map) return null;
  return <EditDrawerInner key={map.uuid} map={map} onClose={onClose} />;
}

function EditDrawerInner({ map, onClose }: { map: MindmapSummary; onClose: () => void }) {
  const updateMap = useUpdateMap(map.uuid);
  const [title, setTitle] = useState(map.title);

  const onSave = (e: FormEvent) => {
    e.preventDefault();
    const next = title.trim();
    if (!next) return;
    updateMap.mutate({ title: next }, { onSuccess: onClose });
  };

  return (
    <Drawer open title="Editar mapa" onClose={onClose}>
      <form onSubmit={onSave} className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700 dark:text-slate-300">Título</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={updateMap.isPending || title.trim().length === 0}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
          >
            Salvar
          </button>
        </div>
        {updateMap.isError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Erro ao salvar: {(updateMap.error as Error).message}
          </p>
        )}
      </form>
    </Drawer>
  );
}
